<template>
  <div class="flex w-full h-appHeader bg-gradient-to-br from-primary to-primaryHeaderGradient">
    <div
      v-if="tablet"
      data-test="header-tablet-view"
      class="w-headerLeftMobile flex items-center justify-between">
      <div
        data-test="header-nav-button"
        @click="triggerNavButton"
        data-ec="header"
        data-et="nav_button">
        <BarsIcon
          data-test="header-burger-icon"
          v-if="!sideMenuActive"
          class="text-whiteOverride w-menuItem h-menuItem cursor-pointer hover:opacity-70 mx-twentyFive"
        />
        <XMarkIcon
          data-test="header-x-icon"
          v-else
          class="text-whiteOverride w-menuItem h-menuItem cursor-pointer hover:opacity-70 mx-twentyFive"
        />
      </div>
      <div class="flex pr-twentyFive border border-t-0 border-l-0 border-b-0 border-whiteOverride border-opacity-50 border-solid">
        <RouterLink
          :to="{ name: 'Envelopes' }"
          data-test="header-s-logo"
          data-ec="header"
          data-et="logo">
          <img
            class="w-appHeaderLogoH select-none"
            :alt="$t('functional.signableLogo')"
            src="@/assets/signable_S.svg" />
        </RouterLink>
      </div>
    </div>
    <div
      v-else
      data-test="header-desktop-view"
      class="w-menuWidth overflow-x-auto flex justify-center items-center">
      <RouterLink
        :to="{ name: 'Envelopes' }"
        data-ec="header"
        data-et="logo">
        <img
         class="w-appHeaderLogoW h-appHeaderLogoH select-none"
         :alt="$t('functional.signableLogo')"
         src="@/assets/signable_full_cropped.svg"
        />
      </RouterLink>
    </div>
    <div
      :class="{
        'shadow-header': !tablet,
        'w-headerRight': !tablet,
        'w-headerRightMobile': tablet,
      }"
      class="h-appHeader flex justify-between items-center">
      <div
        v-if="!tablet"
        class="bg-squiggle2 bg-no-repeat w-appHeaderLeftSquiggleW h-appHeaderLeftSquiggleH absolute top-0 right-appHeaderLeftSquiggleR rotate-23">
      </div>
      <div
        v-if="!mobile"
        class="bg-squiggle2 bg-no-repeat w-appHeaderRightSquiggleW h-appHeaderRightSquiggleH absolute top-appHeaderRightSquiggleT rotate-285 right-40">
      </div>
      <div class="text-whiteOverride relative ml-twentyFive leading-26">
        <h2 class="mb-none">
          <span
            class="select-none"
            data-test="header-title">{{ pageHeader }}
          </span>
        </h2>
        <p
          v-if="!mobile"
          class="select-none"
          data-test="header-subtitle">
          {{ pageSubHeader }}
        </p>
      </div>
      <div
        data-test="header-dropdown-controls"
        @click="toggleDropdown"
        v-click-outside="closeDropdown"
        class="mx-twentyFive flex items-center relative cursor-pointer">
        <span
          v-if="user && !tablet"
          class="text-whiteOverride select-none"
          data-test="header-name"
          data-hj-suppress>
          {{ user.name }}
        </span>
        <div class="relative">
          <div
            ec="header"
            et="menu"
            data-test="header-dropdown"
            v-if="dropdownActive"
            class="shadow-appHeaderDropdown absolute top-ml -right-twenty w-headerDropdownW z-20 bg-whiteOverride rounded-lg">
            <div class="w-base h-base bg-whiteOverride rotate-45 absolute -top-xs right-thirtyOne"></div>
            <ul class="list-none p-none m-none z-20 relative">
              <li
                @click="selectItem(item)"
                v-for="(item, index) in dropdownMenu"
                :data-test="`header-list-item-${item.title}`"
                class="cursor-pointer p-base outline-none hover:bg-sideMenuHover break-all hover:bg-opacity-25 border-l-0 border-t-0 border-r-0 border-b-1 border-sideMenuHover border-solid border-opacity-25"
                :key="index">
                <p>{{ item.title }}</p>
              </li>
            </ul>
          </div>
          <AngleDownIcon class="mx-sm h-twenty w-twenty text-whiteOverride hover:opacity-70 cursor-pointer" />
        </div>
        <div
          v-if="!smallMobile"
          class="select-none shadow-headerInitials bg-whiteOverride w-appHeaderInitialsSize h-appHeaderInitialsSize rounded-full flex items-center justify-center"
          data-test="header-initials">
          {{ user.name | initials }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import initialsFilter from '@/filters/InitialsFilter';
import { BarsIcon, XMarkIcon, AngleDownIcon } from '@/components/icons/fontIcons';

export default {
  name: 'SignableHeader',
  components: {
    BarsIcon,
    XMarkIcon,
    AngleDownIcon,
  },
  props: {
    windowWidth: {
      type: Number,
      required: true,
    },
    sideMenuActive: {
      required: true,
      type: Boolean,
    },
    navButton: {
      required: false,
      type: Function,
      default: () => ({}),
    },
    user: {
      required: true,
      type: [Object],
    },
    pageSubHeader: {
      required: true,
      type: [String],
    },
    pageHeader: {
      required: true,
      type: [String],
    },
    dropdownMenu: {
      required: true,
      type: [Array],
    },
  },
  filters: {
    initials: initialsFilter,
  },
  data() {
    return {
      scroll: {
        last: null,
        direction: null,
        menuPoint: 0,
        menuFixed: false,
      },
      dropdownActive: false,
    };
  },
  computed: {
    tablet() {
      return !!(this.windowWidth && this.windowWidth < 1280);
    },
    mobile() {
      return !!(this.windowWidth && this.windowWidth < 600);
    },
    smallMobile() {
      return !!(this.windowWidth && this.windowWidth < 420);
    },
  },
  methods: {
    selectItem(listItem) {
      if (listItem.route) {
        this.$router.push({
          path: listItem.route,
        });
      } else if (listItem.action) {
        listItem.action();
      }
    },
    toggleDropdown() {
      this.dropdownActive = !this.dropdownActive;
    },
    closeDropdown() {
      this.dropdownActive = false;
    },
    triggerNavButton() {
      this.scroll.menuPoint = document.documentElement.scrollTop;
      this.navButton();
    },
    hideSideNavigation() {
      this.$bus.$emit('hideSideNavigation');
    },
  },
};
</script>

